import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { translations, type Lang } from "./translations";

const STORAGE_KEY = "tcg-lang";
const VALID: Lang[] = ["en", "es", "pt", "de"];

type LanguageCtx = {
    lang: Lang;
    setLang: (l: Lang) => void;
    t: (key: string) => string;
};

const LanguageContext = createContext<LanguageCtx | null>(null);

function readInitial(): Lang {
    try {
        const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
        if (stored && VALID.includes(stored)) return stored;
    } catch { /* ignore */ }
    return "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [lang, setLang] = useState<Lang>(readInitial);

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
        document.documentElement.lang = lang;
    }, [lang]);

    const t = (key: string): string => {
        const dict = translations[lang] ?? translations.en;
        return dict[key] ?? translations.en[key] ?? key;
    };

    return (
        <LanguageContext.Provider value={{ lang, setLang, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(): LanguageCtx {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
    return ctx;
}